import { useCallback, useEffect, useState } from 'react';
import {
  createSearchEngine,
  searchEpisodes
} from '@models/search/searchModel';
import { fetchSearchIndex } from '@models/search/searchRepository';

const initialState = {
  searchIndex: null,
  searchEngine: null,
  loading: true,
  error: null
};

function isAbortError(error) {
  return error?.name === 'AbortError';
}

export function useSearchController() {
  const [state, setState] = useState(initialState);

  const loadSearchIndex = useCallback(async (signal) => {
    setState((currentState) => ({
      ...currentState,
      loading: true,
      error: null
    }));

    try {
      const searchIndex = await fetchSearchIndex({ signal });
      if (signal?.aborted) {
        return;
      }
      setState({
        searchIndex,
        searchEngine: createSearchEngine(searchIndex),
        loading: false,
        error: null
      });
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }

      console.error('Error loading search index:', error);
      setState((currentState) => ({
        ...currentState,
        loading: false,
        error: error.message
      }));
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadSearchIndex(controller.signal);

    return () => controller.abort();
  }, [loadSearchIndex]);

  const search = useCallback(
    (query) => searchEpisodes(state.searchEngine, query),
    [state.searchEngine]
  );

  const reload = useCallback(() => loadSearchIndex(), [loadSearchIndex]);

  return {
    searchIndex: state.searchIndex,
    loading: state.loading,
    error: state.error,
    search,
    reload
  };
}
